import { Button, Tooltip, useConfirmModal } from '@sb/design-system';
import type {
  RestoreConfirmationInput,
  RestoreDifference,
} from '@sb/feathers-types';

interface RestoreConfirmationActionsProps {
  commandId: string;
  /** The operator can still answer: offer decline and confirm. */
  isAwaiting: boolean;
  /** The restore has ended — applied, failed, or refused: offer to dismiss
   *  it from the screen. */
  isFinished: boolean;
  /** Label for the dismiss button. A declined restore did not run, so
   *  "Done" would overstate it. */
  dismissLabel?: string;
  /** Set when the backup was taken on a different software build. The
   *  confirm then goes through an extra prompt naming both versions. */
  buildDifference?: RestoreDifference;
  /** True when a difference will make the robot refuse the restore anyway. */
  isBlocked: boolean;
  /** Why the confirm is unavailable, when it is. Shown on hover, because a
   *  greyed-out destructive button with no stated reason reads as broken. */
  blockedReason?: string;
  hasAcknowledged: boolean;
  isSubmitting: boolean;
  selectedTrainingRunIds: string[];
  onSubmit: (input: RestoreConfirmationInput) => void;
  /** Set when the operator may put the modal aside: while the robot is busy
   *  so they can go and stop it, and once they have answered but the engine
   *  has not picked it up — a dead updater-bot must not lock them out. */
  onSnooze?: () => void;
  /** Label for that button. "Not now" reads wrong once the answer is in. */
  snoozeLabel?: string;
}

/** The modal's answer bar. Nothing is rendered while the restore is applying
 *  — there is no decision left to make until it ends. */
export function RestoreConfirmationActions({
  commandId,
  isAwaiting,
  isFinished,
  dismissLabel = 'Done',
  buildDifference,
  isBlocked,
  blockedReason,
  hasAcknowledged,
  isSubmitting,
  selectedTrainingRunIds,
  onSubmit,
  onSnooze,
  snoozeLabel = 'Not now',
}: RestoreConfirmationActionsProps) {
  // Declining is one click and cannot be taken back from the robot: the
  // command goes terminal in the cloud ledger, and running it after all
  // means someone ordering a fresh one from Marvin. Confirming is already
  // gated behind the acknowledgement toggle; this gives refusing a
  // comparable moment of thought, and says where the way back is.
  const [declineModal, openDeclineModal] = useConfirmModal({
    title: 'Decline this restore?',
    body: 'Nothing on this robot will be changed. This restore is finished once you decline — running it after all means ordering it again from Marvin.',
    cancelLabel: 'Keep deciding',
    confirmLabel: 'Decline restore',
    contentWidth: 400,
    testId: 'restore-confirmation-decline-modal',
    onConfirm: () => onSubmit({ commandId, decision: 'decline' }),
  });

  const confirm = () =>
    onSubmit({
      commandId,
      decision: 'confirm',
      trainingRunIds: selectedTrainingRunIds,
    });

  // A software-build mismatch gets its own prompt rather than only a row in
  // the differences list. The backup carries that version's database, and a
  // version that disagrees with this one about its shape is the difference
  // most likely to leave the robot unable to come back up — unlike an arm
  // serial or an operation mode, it is not something the operator can look
  // at the robot and judge. `autoConfirm` when the builds match, so the
  // usual press still goes straight through.
  const [buildWarningModal, openBuildWarning] = useConfirmModal({
    title: 'This backup is from different software',
    body: buildDifference && (
      <span className="tw-flex tw-flex-col tw-gap-8 tw-text-left">
        {/* The same label/value phrasing as the differences row, rather
            than a sentence: either side can come back as a placeholder
            when the version cannot be read, and "this robot is running
            Unavailable" is not a sentence. */}
        <span>
          Backup{' '}
          <b data-testid="restore-confirmation-build-warning-checkpoint">
            {buildDifference.checkpointValue}
          </b>
          {' · this robot '}
          <b data-testid="restore-confirmation-build-warning-local">
            {buildDifference.localValue}
          </b>
        </span>

        <span>
          Restoring it puts that version&apos;s database on this one. Where the
          two disagree about its shape, the robot may not come back up, and
          putting it right means installing the matching software first.
        </span>
      </span>
    ),
    cancelLabel: 'Go back',
    confirmLabel: 'Erase and restore',
    contentWidth: 400,
    buttonClassName: 'tw-rounded-6',
    testId: 'restore-confirmation-build-warning-modal',
    autoConfirm: !buildDifference,
    onConfirm: confirm,
  });

  if (!isAwaiting && !isFinished && !onSnooze) {
    return null;
  }

  return (
    <div className="tw-flex tw-gap-8 tw-px-16 tw-pb-16 tw-pt-4">
      {declineModal}
      {buildWarningModal}

      {onSnooze && (
        <Button
          className="tw-flex-1 tw-rounded-6"
          size={44}
          data-testid="restore-confirmation-snooze-button"
          disabled={isSubmitting}
          onClick={onSnooze}
        >
          {snoozeLabel}
        </Button>
      )}

      {isAwaiting && (
        <>
          <Button
            className="tw-flex-1 tw-rounded-6"
            size={44}
            data-testid="restore-confirmation-decline-button"
            disabled={isSubmitting}
            onClick={openDeclineModal}
          >
            Don&apos;t restore
          </Button>

          <Tooltip
            className="tw-flex-1"
            content={blockedReason}
            // Shown promptly: this answers "why can't I press this?", and a
            // 1.5s dwell is long enough that people give up and ask instead.
            delayMS={300}
          >
            <Button
              className="tw-w-full tw-rounded-6"
              size={44}
              variant="Filled"
              color="Red"
              data-testid="restore-confirmation-confirm-button"
              disabled={isSubmitting || !hasAcknowledged || isBlocked}
              onClick={openBuildWarning}
            >
              Erase and restore
            </Button>
          </Tooltip>
        </>
      )}

      {isFinished && (
        <Button
          className="tw-flex-1 tw-rounded-6"
          size={44}
          variant="Filled"
          data-testid="restore-confirmation-dismiss-button"
          disabled={isSubmitting}
          onClick={() => onSubmit({ commandId, decision: 'acknowledge' })}
        >
          {dismissLabel}
        </Button>
      )}
    </div>
  );
}
